import { useEffect, useState } from 'react';
import type { BaseAssetType } from '../types/BaseAssetType';
import { getBaseAssetTypes } from '../api/baseAssetTypes';

// Module-level cache (see useAssetTypes for the canonical pattern). Fetched
// lazily when first used (AssetsPage / AssetRecordPage dropdowns); cleared on
// logout via clearBaseAssetTypesCache().
let cache: BaseAssetType[] | null = null;
let inflight: Promise<BaseAssetType[]> | null = null;

function loadBaseAssetTypes(): Promise<BaseAssetType[]> {
  if (cache) return Promise.resolve(cache);
  if (inflight) return inflight;
  inflight = getBaseAssetTypes()
    .then((data) => {
      cache = data;
      inflight = null;
      return data;
    })
    .catch((err: unknown) => {
      inflight = null;
      throw err;
    });
  return inflight;
}

export function clearBaseAssetTypesCache(): void {
  cache = null;
  inflight = null;
}

interface UseBaseAssetTypesResult {
  data: BaseAssetType[] | null;
  loading: boolean;
  error: string | null;
}

export function useBaseAssetTypes(): UseBaseAssetTypesResult {
  const [state, setState] = useState<UseBaseAssetTypesResult>(() =>
    cache
      ? { data: cache, loading: false, error: null }
      : { data: null, loading: true, error: null },
  );

  useEffect(() => {
    if (cache) {
      setState((prev) =>
        prev.data === cache
          ? prev
          : { data: cache, loading: false, error: null },
      );
      return;
    }

    let cancelled = false;
    loadBaseAssetTypes()
      .then((data) => {
        if (cancelled) return;
        setState({ data, loading: false, error: null });
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        const message = err instanceof Error ? err.message : String(err);
        setState({ data: null, loading: false, error: message });
      });

    return () => {
      cancelled = true;
    };
  }, []);

  return state;
}
